/**
 * AdminOverviewScreen — landing page for the district console.
 *
 * Shows high-level metrics: total members, active invites, audit
 * log entries in the last 30 days. Designed for a district IT
 * admin's first click of the day — "is everything OK?".
 */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { supabase } from '../../lib/supabase/client';
import { KpiGridSkeleton } from '../../components/Skeleton';
import { humanError } from '../../lib/ui/errorCopy';

interface Metrics {
  members: number;
  activeInvites: number;
  auditEntriesLast30d: number;
}

interface Props {
  orgId: string;
}

export function AdminOverviewScreen({ orgId }: Props): ReactElement {
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
        const [membersResp, invitesResp, auditResp] = await Promise.all([
          supabase
            .from('organization_memberships')
            .select('user_id', { count: 'exact', head: true })
            .eq('organization_id', orgId),
          supabase
            .from('organization_invites')
            .select('id', { count: 'exact', head: true })
            .eq('organization_id', orgId)
            .is('used_at', null)
            .gt('expires_at', new Date().toISOString()),
          supabase
            .from('tenant_access_log')
            .select('accessed_at', { count: 'exact', head: true })
            .eq('target_organization_id', orgId)
            .gt('accessed_at', since),
        ]);
        if (cancelled) return;
        const firstError = membersResp.error ?? invitesResp.error ?? auditResp.error;
        if (firstError) {
          setError(humanError(firstError));
          setLoading(false);
          return;
        }
        setMetrics({
          members: membersResp.count ?? 0,
          activeInvites: invitesResp.count ?? 0,
          auditEntriesLast30d: auditResp.count ?? 0,
        });
      } catch (err) {
        if (!cancelled) setError(humanError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  if (loading) return <KpiGridSkeleton />;
  if (error) {
    return (
      <section className="empty-state" aria-live="assertive">
        <h2 className="empty-state-title">Couldn&rsquo;t load the overview</h2>
        <p className="empty-state-body">{error}</p>
        <button
          type="button"
          className="snap-button empty-state-cta"
          onClick={() => {
            window.location.reload();
          }}
        >
          Try again
        </button>
      </section>
    );
  }

  return (
    <div className="admin-overview">
      <h2>District at a glance</h2>
      <dl className="metric-grid">
        <div className="metric">
          <dt>Members</dt>
          <dd>{metrics?.members ?? 0}</dd>
        </div>
        <div className="metric">
          <dt>Active invites</dt>
          <dd>{metrics?.activeInvites ?? 0}</dd>
        </div>
        <div className="metric">
          <dt>Audit entries (30 days)</dt>
          <dd>{metrics?.auditEntriesLast30d ?? 0}</dd>
        </div>
      </dl>
      <p className="tagline">
        Use the navigation above to mint invites, view members, or
        export an audit log for FERPA compliance review.
      </p>
    </div>
  );
}